import { buildFilterChips } from '#lib/tableFilters';

import { formatEuro } from './formatters';

const SEARCH_FILTER = { type: 'string', label: 'Suche' };

const TAG_MATCH_OPTIONS = [
	{ value: 'any', label: 'Eines' },
	{ value: 'all', label: 'Alle' },
];

const namesOf = (items, getId, getName) => {
	const nameById = new Map(items.map((item) => [getId(item), getName(item)]));
	return (ids) => ids.map((id) => nameById.get(id) ?? '…').join(', ');
};

const tagNames = (tags) =>
	namesOf(
		tags,
		(tag) => tag.id,
		(tag) => tag.name,
	);

export const TRASH_FILTER_SCHEMA = { search: SEARCH_FILTER };

export const buildCompanyFilterSchema = (tags) => ({
	search: SEARCH_FILTER,
	tagIds: { type: 'idList', label: 'Tags', formatValue: tagNames(tags) },
});

/**
 * URL keys equal the deal list's query-param names, so the parsed filters map 1:1 onto the API.
 *
 * @param {Object} lookups
 * @param {Array<{userId: string, fullName?: string, email?: string}>} lookups.members
 * @param {Array<{id: string, name: string}>} lookups.tags
 * @returns {Record<string, object>}
 */
export const buildDealFilterSchema = ({ members, tags }) => ({
	userIds: {
		type: 'idList',
		label: 'Besitzer',
		formatValue: namesOf(
			members,
			(member) => member.userId,
			(member) => member.fullName ?? member.email,
		),
	},
	tagIds: { type: 'idList', label: 'Tags', formatValue: tagNames(tags) },
	tagMatch: { type: 'enum', label: 'Tag-Verknüpfung', options: TAG_MATCH_OPTIONS },
	valueMin: { type: 'number', label: 'Wert ab', formatValue: (value) => formatEuro(value) },
	valueMax: { type: 'number', label: 'Wert bis', formatValue: (value) => formatEuro(value) },
	probMin: { type: 'number', label: 'Wahrscheinlichkeit ab', formatValue: (value) => `${value} %` },
	hotDealsOnly: { type: 'bool', label: 'Nur heiße Deals' },
	overdueActivitiesOnly: { type: 'bool', label: 'Nur mit überfälligen Aktivitäten' },
	inactiveDealsOnly: { type: 'bool', label: 'Inaktive Deals' },
});

/**
 * Deal list query params (also the `filters` body of select-all bulk requests): id lists stay
 * comma strings, numbers and booleans keep their type, `tagMatch` only counts with 2+ tags.
 *
 * @param {Object} filters - Parsed deal filters (see buildDealFilterSchema)
 * @returns {Object}
 */
export const dealFiltersToApiParams = (filters) => {
	const params = {};
	for (const [key, value] of Object.entries(filters)) {
		if (key === 'tagMatch') continue;
		params[key] = Array.isArray(value) ? value.join(',') : value;
	}
	if (filters.tagMatch && filters.tagIds?.length > 1) params.tagMatch = filters.tagMatch;
	return params;
};

export const countDealFilters = (filters) => Object.keys(filters).filter((key) => key !== 'tagMatch').length;

/**
 * Like buildFilterChips, but the tag-match mode joins the tag chip instead of standing alone,
 * so removing the tags also drops the mode.
 *
 * @param {Object} filters
 * @param {Record<string, object>} schema - From buildDealFilterSchema
 * @returns {Array<{id: string, label: string, paramKeys: string[]}>}
 */
export const buildDealFilterChips = (filters, schema) =>
	buildFilterChips(filters, schema)
		.filter((chip) => chip.id !== 'tagMatch')
		.map((chip) => {
			if (chip.id !== 'tagIds') return chip;
			const matchAll = filters.tagMatch === 'all' && filters.tagIds.length > 1;
			return {
				...chip,
				label: matchAll ? `${chip.label} (alle)` : chip.label,
				paramKeys: ['tagIds', 'tagMatch'],
			};
		});
